import Swal from "sweetalert2";
import { exportTableData } from "../../../../../utils/commonFunction";
import { parsePrerequisiteIds, resolveNetsuiteProductName } from "../../../../../utils/VersionModulesUtils";

const HEADERS = ["Portal Name", "Code", "Cr", "Study Path", "Term", "Year", "Programmes", "NetSuite Product", "Pre-Requisites"];

/**
 * Formats the currently-filtered (not just the current page's) module rows into export-ready
 * objects, resolving the same NetSuite product name and prerequisite names the table cells show.
 */
function buildRows({ rows, netsuiteList, moduleNameById }) {
    return rows.map((row) => {
        const prerequisiteNames = parsePrerequisiteIds(row)
            .map((prerequisiteId) => moduleNameById.get(prerequisiteId))
            .filter(Boolean);

        return {
            "Portal Name": row.Portal || "-",
            Code: row.Code || "-",
            Cr: row.credits ?? "-",
            "Study Path": row.studyPath || "No Study Path",
            Term: row.term ?? "-",
            Year: row.year ?? "-",
            Programmes: row.Programmes ?? 0,
            "NetSuite Product": resolveNetsuiteProductName(netsuiteList, row.netsuite_product),
            "Pre-Requisites": prerequisiteNames.length ? prerequisiteNames.join(", ") : "-",
        };
    });
}

/**
 * DataTable `exportOptions` (Excel, CSV, PDF) for the Version Modules table. Exports every module
 * matching the current search/filters, not only the visible page, and is disabled until NetSuite
 * product names resolve (otherwise exported rows would show "-" for NetSuite Product).
 * @param {object} hook State from `useVersionModules` (or the Assign Module modal's equivalent).
 * @param {string} [fileName="PMC_Version_Modules"] Downloaded file's name, without extension.
 */
export function getVersionModulesExportOptions(hook, fileName = "PMC_Version_Modules") {
    const disabledReason = hook.isProcessing
        ? "A bulk action is currently running"
        : hook.isNetsuiteLoading
            ? "NetSuite product data is still loading"
            : hook.loading
                ? "Modules are loading"
                : hook.isError
                    ? "Modules couldn't be loaded"
                    : hook.totalRows === 0
                        ? "There are no modules to export"
                        : undefined;

    const exportData = async (fileType) => {
        try {
            Swal.fire({
                title: "File downloading",
                onOpen: () => {
                    Swal.showLoading();
                },
            });

            const formattedRows = buildRows({
                rows: hook.allFilteredRows,
                netsuiteList: hook.netsuiteList,
                moduleNameById: hook.moduleNameById,
            });

            await exportTableData(fileType, fileName, HEADERS, formattedRows);
            Swal.close();
        } catch (error) {
            console.error("VersionModulesExport.js->exportData->Error", error);
            Swal.close();
            Swal.fire({
                icon: "error",
                title: "Error",
            });
        }
    };

    return [
        { key: "excel", icon: "file-excel", ariaLabel: "Export as Excel", fileType: "xlsx" },
        { key: "csv", icon: "file-csv", ariaLabel: "Export as CSV", fileType: "csv" },
        { key: "pdf", icon: "file-pdf", ariaLabel: "Export as PDF", fileType: "pdf" },
    ].map(({ fileType, ...option }) => ({
        ...option,
        disabled: Boolean(disabledReason),
        disabledReason,
        onClick: () => exportData(fileType),
    }));
}
